import { useState, useEffect, useCallback, useRef } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";

/**
 * Hook to manage edit locks for a specific record
 * @param {string} modelType - The model type (e.g., 'service', 'client', 'invoice')
 * @param {string|number} recordId - The ID of the record to lock (can be null for new records)
 * @returns {object} Lock state and control functions
 */
export const useEditLock = (modelType, recordId) => {
  const { socket } = useWebSocket();
  const { currentUser } = useAuth();
  const [isLocked, setIsLocked] = useState(false);
  const [lockedBy, setLockedBy] = useState(null);
  const [lockAcquired, setLockAcquired] = useState(false);
  const hasAttemptedLock = useRef(false);
  // Mirrors lockAcquired for use inside socket callbacks (stable ref).
  const lockAcquiredRef = useRef(false);

  // Request lock acquisition
  const acquireLock = useCallback(() => {
    if (!socket || !modelType || !recordId || !currentUser) return;

    socket.emit("edit:lock", {
      modelType,
      recordId: String(recordId),
      user: {
        id: currentUser.id,
        name: currentUser.Name,
        imageThumbnail: currentUser.ImageThumbnail,
      },
    });
  }, [socket, modelType, recordId, currentUser]);

  // Release lock
  const releaseLock = useCallback(() => {
    if (!socket || !modelType || !recordId) return;

    socket.emit("edit:unlock", {
      modelType,
      recordId: String(recordId),
    });

    setLockAcquired(false);
    lockAcquiredRef.current = false;
    setIsLocked(false);
    setLockedBy(null);
  }, [socket, modelType, recordId]);

  // Check lock status
  const checkLock = useCallback(() => {
    if (!socket || !modelType || !recordId) return;

    socket.emit("edit:check", {
      modelType,
      recordId: String(recordId),
    });
  }, [socket, modelType, recordId]);

  useEffect(() => {
    if (!socket || !modelType || !recordId) return;

    // Join the lock room for this record
    const roomName = `lock:${modelType}:${recordId}`;
    socket.emit("join", { room: roomName });

    // Re-join the lock room and re-sync lock state after a socket reconnect —
    // socket.io drops room membership on reconnect and this effect does not
    // re-run (socket identity is stable), so without this the lock dialog
    // would never clear after a reconnect.
    const handleReconnect = () => {
      socket.emit("join", { room: roomName });
      checkLock();
      if (lockAcquiredRef.current) {
        acquireLock();
      }
    };
    socket.on("connect", handleReconnect);

    // Handle lock granted
    const handleGranted = (data) => {
      if (
        data.modelType === modelType &&
        String(data.recordId) === String(recordId)
      ) {
        setLockAcquired(true);
        lockAcquiredRef.current = true;
        setIsLocked(false);
        setLockedBy(null);
        console.info(`[EditLock] Lock acquired for ${modelType}:${recordId}`);
      }
    };

    // Handle lock denied
    const handleDenied = (data) => {
      if (
        data.modelType === modelType &&
        String(data.recordId) === String(recordId)
      ) {
        setLockAcquired(false);
        lockAcquiredRef.current = false;
        setIsLocked(true);
        setLockedBy(data.lockedBy);
        console.info(
          `[EditLock] Lock denied for ${modelType}:${recordId} - locked by ${data.lockedBy.userName}`,
        );
      }
    };

    // Handle record locked by someone else
    const handleLocked = (data) => {
      if (
        data.modelType === modelType &&
        String(data.recordId) === String(recordId)
      ) {
        // Only set locked if it's not us
        if (data.lockedBy.userId !== currentUser?.id) {
          setIsLocked(true);
          setLockedBy(data.lockedBy);
          setLockAcquired(false);
          lockAcquiredRef.current = false;
          console.info(
            `[EditLock] ${modelType}:${recordId} locked by ${data.lockedBy.userName}`,
          );
        }
      }
    };

    // Handle record unlocked
    const handleUnlocked = (data) => {
      if (
        data.modelType === modelType &&
        String(data.recordId) === String(recordId)
      ) {
        setIsLocked(false);
        setLockedBy(null);
        console.info(`[EditLock] ${modelType}:${recordId} unlocked`);
      }
    };

    // Handle lock status check response
    const handleStatus = (data) => {
      if (
        data.modelType === modelType &&
        String(data.recordId) === String(recordId)
      ) {
        if (data.isLocked && data.lockedBy.userId !== currentUser?.id) {
          setIsLocked(true);
          setLockedBy(data.lockedBy);
          setLockAcquired(false);
          lockAcquiredRef.current = false;
        } else {
          setIsLocked(false);
          setLockedBy(null);
        }
      }
    };

    socket.on("edit:granted", handleGranted);
    socket.on("edit:denied", handleDenied);
    socket.on("edit:locked", handleLocked);
    socket.on("edit:unlocked", handleUnlocked);
    socket.on("edit:status", handleStatus);

    // Attempt to acquire lock on mount (only for existing records)
    if (!hasAttemptedLock.current) {
      hasAttemptedLock.current = true;
      checkLock(); // First check status
      setTimeout(() => acquireLock(), 100); // Then try to acquire
    }

    return () => {
      socket.off("edit:granted", handleGranted);
      socket.off("edit:denied", handleDenied);
      socket.off("edit:locked", handleLocked);
      socket.off("edit:unlocked", handleUnlocked);
      socket.off("edit:status", handleStatus);
      socket.off("connect", handleReconnect);
    };
  }, [socket, modelType, recordId, currentUser, acquireLock, checkLock]);

  // Renew the lock periodically while held so long edits don't hit the
  // server-side TTL (locks expire after 5 minutes without renewal).
  useEffect(() => {
    if (!socket || !lockAcquired || !modelType || !recordId || !currentUser) return;

    const renewTimer = setInterval(() => {
      socket.emit("edit:lock", {
        modelType,
        recordId: String(recordId),
        user: {
          id: currentUser.id,
          name: currentUser.Name,
          imageThumbnail: currentUser.ImageThumbnail,
        },
      });
    }, 60 * 1000);

    return () => clearInterval(renewTimer);
  }, [socket, lockAcquired, modelType, recordId, currentUser]);

  // Auto-release lock on unmount or when recordId changes
  useEffect(() => {
    return () => {
      if (lockAcquired) {
        releaseLock();
      }
    };
  }, [lockAcquired, releaseLock]);

  return {
    isLocked, // Record is locked by someone else
    lockedBy, // Who locked it { userId, userName, imageThumbnail }
    lockAcquired, // Current user has acquired the lock
    acquireLock, // Function to manually request lock
    releaseLock, // Function to manually release lock
    checkLock, // Function to check lock status
  };
};
